/**
 * Catalog audit viewer (ADMIN-PR2-a) — GET /v1/admin/audit?organization_id=&limit=
 * (super_admin / trustee). Filter by org, cap with limit; expand before/after JSON.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import { listAudit, type AuditLogOut } from "../auth/mastersApi";
import { errorMessage, useAsync } from "../lib/useAsync";
import { EmptyState, ErrorBanner, PageHeader, Spinner } from "../components/ui";

function fmtTs(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function AuditPage(): ReactNode {
  const [orgInput, setOrgInput] = useState("");
  const [orgId, setOrgId] = useState("");
  const [limit, setLimit] = useState(50);

  const { data, loading, error } = useAsync<AuditLogOut[]>(
    () => listAudit({ organization_id: orgId, limit }),
    [orgId, limit],
  );

  function onFilter(e: FormEvent) {
    e.preventDefault();
    setOrgId(orgInput.trim());
  }

  return (
    <div>
      <PageHeader title="Catalog audit" />

      <form onSubmit={onFilter} className="mb-3 flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Organization ID (optional)</span>
          <input
            value={orgInput}
            onChange={(e) => setOrgInput(e.target.value)}
            placeholder="all organizations"
            className="w-72 rounded border border-slate-300 px-2 py-1.5 font-mono text-xs"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-600">Limit</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            {[25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          className="rounded bg-oxbo px-3 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark"
        >
          Filter
        </button>
      </form>

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorBanner message={errorMessage(error)} />
      ) : !data || data.length === 0 ? (
        <EmptyState message="No audit events." />
      ) : (
        <div className="space-y-2">
          {data.map((row) => (
            <AuditRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function AuditRow({ row }: { row: AuditLogOut }): ReactNode {
  const [open, setOpen] = useState(false);
  const hasDetail = row.before !== null || row.after !== null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white text-sm">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-4 py-2 text-left"
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-slate-500">{fmtTs(row.ts)}</span>
          <span className="font-medium text-slate-800">{row.action}</span>
          <span className="text-slate-500">
            {row.entity}
            {row.entity_id ? `:${row.entity_id.slice(0, 8)}…` : ""}
          </span>
          {row.organization_id && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
              org {row.organization_id.slice(0, 8)}…
            </span>
          )}
        </span>
        {hasDetail && <span className="text-xs text-slate-400">{open ? "▲" : "▼"}</span>}
      </button>
      {open && hasDetail && (
        <div className="grid grid-cols-1 gap-3 border-t border-slate-100 px-4 py-3 sm:grid-cols-2">
          <JsonBlock title="before" value={row.before} />
          <JsonBlock title="after" value={row.after} />
        </div>
      )}
    </div>
  );
}

function JsonBlock({ title, value }: { title: string; value: unknown }): ReactNode {
  return (
    <div>
      <div className="mb-1 text-xs uppercase tracking-wide text-slate-400">{title}</div>
      <pre className="overflow-x-auto rounded bg-slate-50 p-2 text-[11px] text-slate-700">
        {value === null ? "—" : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
